import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TagIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  LinkIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  increment,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { useRole } from '../../../hooks/useRole';
import { useToast } from '../../../context/ToastContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';

const KnowledgeBase = () => {
  const { currentUser } = useAuth();
  const { userRole } = useRole();
  const toast = useToast();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [savingArticle, setSavingArticle] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [newArticle, setNewArticle] = useState({
    title: '',
    description: '',
    category: 'general',
    content: '',
    strategicImplications: '',
    conclusion: '',
    tags: [],
    link: '',
    documents: [],
    steps: [],
  });

  const isCoach = userRole === 'coach' || userRole === 'admin' || userRole === 'superAdmin';

  useEffect(() => {
    setLoading(true);
    let unsubscribe = null;

    // Helper function to process snapshot data
    const processSnapshot = snapshot => {
      const articlesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle Firestore timestamp conversion
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt?.seconds
              ? new Date(data.createdAt.seconds * 1000)
              : data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt?.seconds
              ? new Date(data.updatedAt.seconds * 1000)
              : data.updatedAt || new Date(),
        };
      });

      // Sort manually by createdAt (descending)
      articlesData.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });

      setArticles(articlesData);
      setLoading(false);
    };

    // Try query with orderBy first
    const tryQueryWithOrderBy = () => {
      const articlesQuery = query(
        collection(firestore, 'knowledgeBase'),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(articlesQuery, processSnapshot, error => {
        console.error('Error listening to knowledge base:', error);

        // If orderBy fails due to missing index, try without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.info('Index not yet created, using fallback query');
          tryQueryWithoutOrderBy();
        } else {
          toast.error('Failed to load knowledge base', 'Error');
          setLoading(false);
        }
      });
    };

    // Fallback query without orderBy
    const tryQueryWithoutOrderBy = () => {
      if (unsubscribe) unsubscribe(); // Clean up previous listener

      const simpleQuery = query(collection(firestore, 'knowledgeBase'));

      unsubscribe = onSnapshot(simpleQuery, processSnapshot, fallbackError => {
        console.error('Error in fallback query:', fallbackError);
        toast.error('Failed to load knowledge base', 'Error');
        setLoading(false);
      });
    };

    // Start with orderBy query
    tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All Categories', color: 'gray' },
    { id: 'general', name: 'General', color: 'cyan' },
    { id: 'business', name: 'Business', color: 'blue' },
    { id: 'marketing', name: 'Marketing', color: 'purple' },
    { id: 'finance', name: 'Finance', color: 'emerald' },
    { id: 'legal', name: 'Legal', color: 'amber' },
    { id: 'technology', name: 'Technology', color: 'indigo' },
  ];

  const getCategoryColor = categoryId => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'gray';

    const colorMap = {
      cyan: 'bg-cyan-100 text-cyan-800 border-cyan-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      amber: 'bg-amber-100 text-amber-800 border-amber-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return colorMap[category.color] || colorMap.gray;
  };

  // Helper function to normalize step data (handle backward compatibility)
  const normalizeStep = step => {
    if (typeof step === 'string') {
      return { title: step, description: '' };
    }
    return {
      title: step?.title || '',
      description: step?.description || '',
    };
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newArticle.title || !newArticle.description) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    setSavingArticle(true);
    try {
      // Fetch user profile to get firstname and lastname
      let authorName = currentUser.displayName || currentUser.email;
      let authorFirstName = '';
      let authorLastName = '';

      try {
        // Try to get coach profile first
        const coachDoc = await getDoc(doc(firestore, 'coaches', currentUser.uid));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          authorFirstName = coachData.firstname || '';
          authorLastName = coachData.lastname || '';
          if (authorFirstName || authorLastName) {
            authorName = `${authorFirstName} ${authorLastName}`.trim();
          }
        } else {
          // Try admin collection
          const adminDoc = await getDoc(doc(firestore, 'admin', currentUser.uid));
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            authorFirstName = adminData.firstname || '';
            authorLastName = adminData.lastname || '';
            if (authorFirstName || authorLastName) {
              authorName = `${authorFirstName} ${authorLastName}`.trim();
            }
          } else {
            // Try moderator collection
            const moderatorDoc = await getDoc(doc(firestore, 'moderator', currentUser.uid));
            if (moderatorDoc.exists()) {
              const moderatorData = moderatorDoc.data();
              authorFirstName = moderatorData.firstname || '';
              authorLastName = moderatorData.lastname || '';
              if (authorFirstName || authorLastName) {
                authorName = `${authorFirstName} ${authorLastName}`.trim();
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user profile:', error);
        // Continue with email as fallback
      }

      let documents = [...(newArticle.documents || [])];

      // Upload new documents if any files are selected
      if (documentFiles.length > 0) {
        setUploadingDocuments(true);
        try {
          const uploadPromises = documentFiles.map(async file => {
            const documentRef = ref(storage, `knowledgeBase/documents/${Date.now()}_${file.name}`);
            await uploadBytes(documentRef, file);
            const downloadURL = await getDownloadURL(documentRef);
            return {
              name: file.name,
              url: downloadURL,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
            };
          });

          const uploadedDocs = await Promise.all(uploadPromises);
          documents = [...documents, ...uploadedDocs];
        } catch (error) {
          console.error('Error uploading documents:', error);
          toast.error('Failed to upload some documents', 'Error');
          setUploadingDocuments(false);
          setSavingArticle(false);
          return;
        }
        setUploadingDocuments(false);
      }

      const articleData = {
        ...newArticle,
        documents: documents,
        createdBy: currentUser.email,
        createdByName: authorName,
        createdByFirstName: authorFirstName,
        createdByLastName: authorLastName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Initialize analytics
        views: editingArticle?.views || 0,
        clicks: editingArticle?.clicks || 0,
        lastViewedAt: editingArticle?.lastViewedAt || null,
      };

      if (editingArticle) {
        await updateDoc(doc(firestore, 'knowledgeBase', editingArticle.id), {
          ...articleData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Article updated successfully!');
      } else {
        await addDoc(collection(firestore, 'knowledgeBase'), articleData);
        toast.success('Article created successfully!');
      }

      setShowModal(false);
      setEditingArticle(null);
      setNewArticle({
        title: '',
        description: '',
        category: 'general',
        content: '',
        strategicImplications: '',
        conclusion: '',
        tags: [],
        link: '',
        documents: [],
        steps: [],
      });
      setDocumentFiles([]);
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error('Failed to save article', 'Error');
    } finally {
      setSavingArticle(false);
    }
  };

  const handleDeleteClick = article => {
    setArticleToDelete(article);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;

    try {
      // Delete documents from storage if they exist
      if (articleToDelete.documents && articleToDelete.documents.length > 0) {
        try {
          const deletePromises = articleToDelete.documents.map(async doc => {
            if (doc.url) {
              const docRef = ref(storage, doc.url);
              await deleteObject(docRef);
            }
          });
          await Promise.all(deletePromises);
        } catch (error) {
          console.warn('Failed to delete some documents:', error);
          // Continue with article deletion even if document deletion fails
        }
      }

      await deleteDoc(doc(firestore, 'knowledgeBase', articleToDelete.id));
      toast.success('Article deleted successfully!');
      setShowDeleteModal(false);
      setArticleToDelete(null);
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article', 'Error');
    }
  };

  const handleEdit = article => {
    setEditingArticle(article);
    // Normalize steps to ensure they're objects
    const normalizedSteps = (article.steps || []).map(step => normalizeStep(step));
    setNewArticle({
      title: article.title || '',
      description: article.description || '',
      category: article.category || 'general',
      content: article.content || '',
      strategicImplications: article.strategicImplications || '',
      conclusion: article.conclusion || '',
      tags: article.tags || [],
      link: article.link || '',
      documents: article.documents || [],
      steps: normalizedSteps,
    });
    setDocumentFiles([]);
    setShowModal(true);
  };

  const handleCardClick = async article => {
    setSelectedArticle(article);
    setShowDetailModal(true);

    // Track view analytics
    try {
      const articleRef = doc(firestore, 'knowledgeBase', article.id);
      await updateDoc(articleRef, {
        views: increment(1),
        lastViewedAt: serverTimestamp(),
      });

      // Log detailed analytics event
      await addDoc(collection(firestore, 'knowledgeBaseAnalytics'), {
        articleId: article.id,
        articleTitle: article.title,
        userId: currentUser?.uid || 'anonymous',
        userEmail: currentUser?.email || 'anonymous',
        userRole: userRole || 'unknown',
        action: 'view',
        timestamp: serverTimestamp(),
        metadata: {
          category: article.category,
          hasDocuments: !!(article.documents && article.documents.length > 0),
          hasContent: !!article.content,
          hasLink: !!article.link,
        },
      });
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const handleDocumentUpload = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = [];
    const maxSize = 50 * 1024 * 1024; // 50MB per file

    files.forEach(file => {
      // Allow common document types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv',
      ];

      if (
        !allowedTypes.includes(file.type) &&
        !file.name.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/i)
      ) {
        toast.error(`File "${file.name}" is not a supported document type`, 'Error');
        return;
      }

      if (file.size > maxSize) {
        toast.error(`File "${file.name}" exceeds 50MB limit`, 'Error');
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setDocumentFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} document(s) added`, 'Success');
    }
  };

  const removeDocumentFile = index => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = async (articleId, docIndex) => {
    try {
      const article = articles.find(a => a.id === articleId);
      if (!article || !article.documents || !article.documents[docIndex]) return;

      const docToDelete = article.documents[docIndex];

      // Delete from storage
      if (docToDelete.url) {
        try {
          const docRef = ref(storage, docToDelete.url);
          await deleteObject(docRef);
        } catch (error) {
          console.warn('Failed to delete document from storage:', error);
        }
      }

      // Update article to remove document
      const updatedDocuments = article.documents.filter((_, i) => i !== docIndex);
      await updateDoc(doc(firestore, 'knowledgeBase', articleId), {
        documents: updatedDocuments,
        updatedAt: serverTimestamp(),
      });

      toast.success('Document removed successfully');
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove document', 'Error');
    }
  };

  const addStep = () => {
    setNewArticle({
      ...newArticle,
      steps: [...(newArticle.steps || []), { title: '', description: '' }],
    });
  };

  const updateStep = (index, field, value) => {
    const updatedSteps = [...(newArticle.steps || [])];
    // Handle backward compatibility - convert string steps to objects
    if (typeof updatedSteps[index] === 'string') {
      updatedSteps[index] = { title: updatedSteps[index], description: '' };
    }
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };
    setNewArticle({ ...newArticle, steps: updatedSteps });
  };

  const removeStep = index => {
    const updatedSteps = newArticle.steps.filter((_, i) => i !== index);
    setNewArticle({ ...newArticle, steps: updatedSteps });
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch =
      article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-cyan-200 border-t-cyan-600"></div>
            <p className="font-medium text-gray-600">Loading knowledge base...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white p-4 shadow-xl sm:p-6"
    >
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg">
            <BookOpenIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Knowledge Base</h2>
            <p className="text-sm text-gray-500">Educational resources and learning materials</p>
          </div>
        </div>
        {isCoach && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingArticle(null);
              setNewArticle({
                title: '',
                description: '',
                category: 'general',
                content: '',
                strategicImplications: '',
                conclusion: '',
                tags: [],
                link: '',
                documents: [],
                steps: [],
              });
              setDocumentFiles([]);
              setShowModal(true);
            }}
            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-cyan-600 hover:to-blue-700 hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Add Article</span>
          </motion.button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search knowledge base..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        {/* View Mode Toggle */}
        <div className="flex rounded-xl border border-gray-300 bg-white p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'grid'
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Grid</span>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Table</span>
          </button>
        </div>
      </div>

      {/* Articles View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filteredArticles.map((article, index) => (
              <motion.div
                key={article.id}
                layout
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{
                  delay: index * 0.05,
                  layout: { duration: 0.3 },
                }}
                onClick={() => handleCardClick(article)}
                className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-white to-cyan-50/30 shadow-sm transition-all hover:scale-[1.02] hover:border-cyan-300 hover:shadow-lg"
              >
                <div className="relative p-6">
                  <div className="mb-4 flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                    {isCoach && (
                      <div className="flex items-center gap-1">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={e => {
                            e.stopPropagation();
                            handleEdit(article);
                          }}
                          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-cyan-600"
                          aria-label="Edit article"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteClick(article);
                          }}
                          className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600"
                          aria-label="Delete article"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </motion.button>
                      </div>
                    )}
                  </div>

                  <h3 className="mb-2 text-xl font-bold text-gray-900 transition-colors group-hover:text-cyan-600">
                    {article.title}
                  </h3>
                  <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-gray-600">
                    {article.description}
                  </p>

                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(article.category)}`}
                    >
                      <TagIcon className="h-3 w-3" />
                      {categories.find(c => c.id === article.category)?.name || article.category}
                    </span>
                    {article.createdAt && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <ClockIcon className="h-3 w-3" />
                        <span>
                          {article.createdAt?.toDate
                            ? new Date(article.createdAt.toDate()).toLocaleDateString()
                            : article.createdAt instanceof Date
                              ? article.createdAt.toLocaleDateString()
                              : 'Recently'}
                        </span>
                      </div>
                    )}
                  </div>

                  {article.tags && article.tags.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {article.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                        >
                          {tag}
                        </span>
                      ))}
                      {article.tags.length > 3 && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          +{article.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {article.createdBy && (
                    <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                      <UserIcon className="h-3 w-3" />
                      <span>
                        By{' '}
                        {article.createdByFirstName || article.createdByLastName
                          ? `${article.createdByFirstName || ''} ${article.createdByLastName || ''}`.trim()
                          : article.createdByName || ''}{' '}
                        ({article.createdBy})
                      </span>
                    </div>
                  )}

                  {/* Analytics Badge */}
                  {(article.views > 0 || article.clicks > 0) && (
                    <div className="mb-4 flex items-center gap-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <EyeIcon className="h-3 w-3" />
                        <span>{article.views || 0} views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ChartBarIcon className="h-3 w-3" />
                        <span>{article.clicks || 0} clicks</span>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {article.documents && article.documents.length > 0 && (
                      <div className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2">
                        <DocumentArrowDownIcon className="h-4 w-4 text-cyan-600" />
                        <span className="text-xs font-medium text-cyan-700">
                          {article.documents.length} document
                          {article.documents.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                    {article.content && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                        <span className="text-xs font-medium text-gray-700">Detailed content</span>
                      </div>
                    )}
                    {article.link && (
                      <div className="flex items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2">
                        <LinkIcon className="h-4 w-4 text-cyan-600" />
                        <span className="text-xs font-medium text-cyan-700">External link</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Title
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Author
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Analytics
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Date
                  </th>
                  {isCoach && (
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <AnimatePresence>
                  {filteredArticles.map((article, index) => (
                    <motion.tr
                      key={article.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => handleCardClick(article)}
                      className="cursor-pointer transition-colors hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600">
                            <BookOpenIcon className="h-5 w-5 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold text-gray-900">
                              {article.title}
                            </div>
                            {article.description && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleCardClick(article);
                                }}
                                className="mt-1 text-xs font-medium text-cyan-600 hover:text-cyan-700 hover:underline"
                              >
                                See more
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(article.category)}`}
                        >
                          <TagIcon className="h-3 w-3" />
                          {categories.find(c => c.id === article.category)?.name ||
                            article.category}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        {article.createdBy && (
                          <div className="flex items-center gap-2">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span>
                              {article.createdByFirstName || article.createdByLastName
                                ? `${article.createdByFirstName || ''} ${article.createdByLastName || ''}`.trim()
                                : article.createdByName || ''}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          {article.documents && article.documents.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-cyan-600">
                              <DocumentArrowDownIcon className="h-3 w-3" />
                              {article.documents.length} doc
                              {article.documents.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {article.link && (
                            <span className="flex items-center gap-1 text-xs text-cyan-600">
                              <LinkIcon className="h-3 w-3" />
                              Link
                            </span>
                          )}
                          {article.content && (
                            <span className="flex items-center gap-1 text-xs text-cyan-600">
                              <DocumentTextIcon className="h-3 w-3" />
                              Content
                            </span>
                          )}
                          {!article.documents?.length && !article.link && !article.content && (
                            <span className="text-xs text-gray-400">Basic</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <EyeIcon className="h-4 w-4 text-gray-400" />
                            <span>{article.views || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ChartBarIcon className="h-4 w-4 text-gray-400" />
                            <span>{article.clicks || 0}</span>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {article.createdAt?.toDate
                          ? new Date(article.createdAt.toDate()).toLocaleDateString()
                          : article.createdAt instanceof Date
                            ? article.createdAt.toLocaleDateString()
                            : 'Recently'}
                      </td>
                      {isCoach && (
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={e => {
                                e.stopPropagation();
                                handleEdit(article);
                              }}
                              className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                              aria-label="Edit article"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={e => {
                                e.stopPropagation();
                                handleDeleteClick(article);
                              }}
                              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                              aria-label="Delete article"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </motion.button>
                          </div>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredArticles.length === 0 && (
        <div className="py-16 text-center">
          <AcademicCapIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-semibold text-gray-700">
            {searchTerm || selectedCategory !== 'all'
              ? 'No articles match your filters'
              : 'No articles yet'}
          </h3>
          <p className="text-sm text-gray-500">
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your search or filters'
              : isCoach
                ? 'Create your first article to get started!'
                : 'Check back later for articles'}
          </p>
        </div>
      )}

      {/* Article Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedArticle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => {
              setShowDetailModal(false);
              setSelectedArticle(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(selectedArticle.category)}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {categories.find(c => c.id === selectedArticle.category)?.name ||
                          selectedArticle.category}
                      </span>
                      {selectedArticle.createdAt && (
                        <div className="flex items-center gap-1 text-xs text-white/80">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {selectedArticle.createdAt?.toDate
                              ? new Date(selectedArticle.createdAt.toDate()).toLocaleDateString()
                              : selectedArticle.createdAt instanceof Date
                                ? selectedArticle.createdAt.toLocaleDateString()
                                : 'Recently'}
                          </span>
                        </div>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold text-white">{selectedArticle.title}</h2>
                    {selectedArticle.createdBy && (
                      <p className="mt-2 text-sm text-white/90">
                        By{' '}
                        {selectedArticle.createdByFirstName || selectedArticle.createdByLastName
                          ? `${selectedArticle.createdByFirstName || ''} ${selectedArticle.createdByLastName || ''}`.trim()
                          : selectedArticle.createdByName || ''}{' '}
                        ({selectedArticle.createdBy})
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedArticle(null);
                    }}
                    className="rounded-lg p-2 text-white/90 transition-colors hover:bg-white/20"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[calc(90vh-200px)] overflow-y-auto px-6 py-6">
                {/* Description */}
                <div className="mb-6">
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">Description</h3>
                  <p className="leading-relaxed text-gray-700">{selectedArticle.description}</p>
                </div>

                {/* Documents */}
                {selectedArticle.documents && selectedArticle.documents.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <DocumentArrowDownIcon className="h-5 w-5 text-cyan-600" />
                      Documents ({selectedArticle.documents.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedArticle.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100">
                              <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{doc.name}</p>
                              {doc.size && (
                                <p className="text-xs text-gray-500">
                                  {(doc.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              // Track document download analytics
                              const articleRef = doc(
                                firestore,
                                'knowledgeBase',
                                selectedArticle.id
                              );
                              updateDoc(articleRef, {
                                clicks: increment(1),
                              });
                              addDoc(collection(firestore, 'knowledgeBaseAnalytics'), {
                                articleId: selectedArticle.id,
                                articleTitle: selectedArticle.title,
                                userId: currentUser?.uid || 'anonymous',
                                userEmail: currentUser?.email || 'anonymous',
                                userRole: userRole || 'unknown',
                                action: 'document_download',
                                documentName: doc.name,
                                timestamp: serverTimestamp(),
                              }).catch(console.error);
                            }}
                            className="flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-700"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Steps */}
                {selectedArticle.steps && selectedArticle.steps.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <CheckCircleIcon className="h-5 w-5 text-cyan-600" />
                      Steps ({selectedArticle.steps.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedArticle.steps.map((step, idx) => {
                        const normalizedStep = normalizeStep(step);
                        return (
                          <div
                            key={idx}
                            className="flex items-start gap-4 rounded-lg border border-cyan-100 bg-cyan-50/50 p-4"
                          >
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-cyan-600 text-sm font-bold text-white">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <h4 className="mb-1 font-bold text-gray-900">
                                {normalizedStep.title}
                              </h4>
                              {normalizedStep.description && (
                                <p className="leading-relaxed text-gray-700">
                                  {normalizedStep.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Link */}
                {selectedArticle.link && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <LinkIcon className="h-5 w-5 text-cyan-600" />
                      External Link
                    </h3>
                    <a
                      href={selectedArticle.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-700"
                      onClick={() => {
                        // Track link click analytics
                        const articleRef = doc(firestore, 'knowledgeBase', selectedArticle.id);
                        updateDoc(articleRef, {
                          clicks: increment(1),
                        });
                        addDoc(collection(firestore, 'knowledgeBaseAnalytics'), {
                          articleId: selectedArticle.id,
                          articleTitle: selectedArticle.title,
                          userId: currentUser?.uid || 'anonymous',
                          userEmail: currentUser?.email || 'anonymous',
                          userRole: userRole || 'unknown',
                          action: 'link_click',
                          timestamp: serverTimestamp(),
                        }).catch(console.error);
                      }}
                    >
                      <LinkIcon className="h-4 w-4" />
                      Access Link
                    </a>
                  </div>
                )}

                {/* Detailed Content */}
                {selectedArticle.content && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                      Detailed Content
                    </h3>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                        {selectedArticle.content}
                      </p>
                    </div>
                  </div>
                )}

                {/* Strategic Implications */}
                {selectedArticle.strategicImplications && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <AcademicCapIcon className="h-5 w-5 text-cyan-600" />
                      Strategic Implications
                    </h3>
                    <div className="rounded-lg border border-cyan-200 bg-cyan-50/30 p-4">
                      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                        {selectedArticle.strategicImplications}
                      </p>
                    </div>
                  </div>
                )}

                {/* Conclusion */}
                {selectedArticle.conclusion && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <CheckCircleIcon className="h-5 w-5 text-cyan-600" />
                      Conclusion
                    </h3>
                    <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-4">
                      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                        {selectedArticle.conclusion}
                      </p>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 text-lg font-semibold text-gray-900">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics */}
                {(selectedArticle.views > 0 || selectedArticle.clicks > 0) && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <ChartBarIcon className="h-4 w-4" />
                      Analytics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Views</p>
                        <p className="text-2xl font-bold text-cyan-600">
                          {selectedArticle.views || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Clicks</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {selectedArticle.clicks || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              {isCoach && (
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDetailModal(false);
                        handleEdit(selectedArticle);
                      }}
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDetailModal(false);
                        handleDeleteClick(selectedArticle);
                      }}
                      className="flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </motion.button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => {
              setShowModal(false);
              setEditingArticle(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 mb-6 flex items-center justify-between border-b bg-white pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg">
                    <BookOpenIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingArticle ? 'Edit Article' : 'Create Article'}
                    </h3>
                    <p className="text-sm text-gray-500">Add a new article to the knowledge base</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingArticle(null);
                  }}
                  className="rounded-xl p-2 transition-colors hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newArticle.title}
                    onChange={e => setNewArticle({ ...newArticle, title: e.target.value })}
                    placeholder="Enter article title"
                    className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newArticle.description}
                    onChange={e => setNewArticle({ ...newArticle, description: e.target.value })}
                    rows={3}
                    placeholder="Brief description of the article..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Category
                    </label>
                    <select
                      value={newArticle.category}
                      onChange={e => setNewArticle({ ...newArticle, category: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    >
                      {categories
                        .filter(c => c.id !== 'all')
                        .map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      External Link (Optional)
                    </label>
                    <input
                      type="url"
                      value={newArticle.link}
                      onChange={e => setNewArticle({ ...newArticle, link: e.target.value })}
                      placeholder="https://..."
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newArticle.content}
                    onChange={e => setNewArticle({ ...newArticle, content: e.target.value })}
                    rows={10}
                    placeholder="Write the full article content here..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Strategic Implications (Optional)
                  </label>
                  <textarea
                    value={newArticle.strategicImplications}
                    onChange={e =>
                      setNewArticle({ ...newArticle, strategicImplications: e.target.value })
                    }
                    rows={6}
                    placeholder="Describe the strategic implications of this article..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Conclusion (Optional)
                  </label>
                  <textarea
                    value={newArticle.conclusion}
                    onChange={e => setNewArticle({ ...newArticle, conclusion: e.target.value })}
                    rows={6}
                    placeholder="Write a conclusion summarizing key points..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Steps (Optional)
                  </label>
                  <div className="space-y-4">
                    {(newArticle.steps || []).map((step, index) => {
                      const normalizedStep = normalizeStep(step);
                      return (
                        <div
                          key={index}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-sm font-bold text-cyan-600">
                                {index + 1}
                              </span>
                              <span className="text-sm font-semibold text-gray-700">
                                Step {index + 1}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeStep(index)}
                              className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-700">
                                Step Title <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={normalizedStep.title}
                                onChange={e => updateStep(index, 'title', e.target.value)}
                                placeholder={`e.g., Identify the problem`}
                                className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                                required
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-700">
                                Description
                              </label>
                              <textarea
                                value={normalizedStep.description}
                                onChange={e => updateStep(index, 'description', e.target.value)}
                                placeholder={`Describe what to do in this step...`}
                                rows={2}
                                className="w-full resize-none rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={addStep}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-cyan-500 hover:bg-cyan-50 hover:text-cyan-600"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Step
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Documents (Optional)
                  </label>
                  <div className="space-y-3">
                    {/* Existing Documents */}
                    {newArticle.documents && newArticle.documents.length > 0 && (
                      <div className="space-y-2">
                        {newArticle.documents.map((doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100">
                                <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                                {doc.size && (
                                  <p className="text-xs text-gray-500">
                                    {(doc.size / (1024 * 1024)).toFixed(2)} MB
                                  </p>
                                )}
                              </div>
                            </div>
                            {isCoach && editingArticle && (
                              <button
                                type="button"
                                onClick={() => removeDocument(editingArticle.id, idx)}
                                className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* New Files to Upload */}
                    {documentFiles.length > 0 && (
                      <div className="space-y-2">
                        {documentFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-lg border border-cyan-200 bg-cyan-50 p-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100">
                                <DocumentTextIcon className="h-5 w-5 text-cyan-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDocumentFile(idx)}
                              className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="flex items-center gap-3">
                      <label
                        htmlFor="document-upload"
                        className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-cyan-500 hover:bg-cyan-50 hover:text-cyan-600"
                      >
                        <DocumentArrowDownIcon className="h-5 w-5" />
                        <span>
                          {documentFiles.length > 0 ? 'Add More Documents' : 'Upload Documents'}
                        </span>
                      </label>
                      <input
                        id="document-upload"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        onChange={handleDocumentUpload}
                        className="hidden"
                      />
                      {uploadingDocuments && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-200 border-t-cyan-600"></div>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV. Max size:
                      50MB per file. Multiple files allowed.
                    </p>
                  </div>
                </div>

                <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-white pb-2 pt-4 sm:flex-row">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowModal(false);
                      setEditingArticle(null);
                    }}
                    disabled={uploadingDocuments || savingArticle}
                    className="flex-1 rounded-xl border-2 border-gray-300 bg-white px-6 py-3.5 text-sm font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={uploadingDocuments || savingArticle}
                    className="flex-1 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-cyan-600 hover:to-blue-700 hover:shadow-xl disabled:opacity-50"
                  >
                    {uploadingDocuments || savingArticle ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                        {uploadingDocuments
                          ? 'Uploading documents...'
                          : savingArticle
                            ? 'Saving...'
                            : 'Processing...'}
                      </span>
                    ) : editingArticle ? (
                      'Update Article'
                    ) : (
                      'Create Article'
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setArticleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Article"
        message="Are you sure you want to delete this article? This action cannot be undone."
        itemName={articleToDelete?.title}
      />
    </motion.div>
  );
};

export default KnowledgeBase;
